/**
 * Mining runtime (M2 plan §6 M2-16, spec §5): the mining beam, CHIP chunks and the collector cone, the FRACTURE
 * minigame with laser heat, bursts, fragments and depletion, plus the rock queries the tools runtime, HUD and
 * save system use (pick, scan, position, radius, deltas).
 *
 * State:
 * - remaining mass sparse by stable rock id (`FieldLayout.id`; untouched = full mass), mirrored into a
 *   per-layout-index cache that is rebuilt when the sim's layout changes (a preset change or a rebuild) and whose
 *   "gone" flags refresh when `sim.fieldVersion` moves; each rock's composition (mix, mode, resistance, tier) is
 *   computed on first touch and cached;
 * - the chunk pool (`chunks.ts`) and the fragment pool with its fractured-parent groups (`fragments.ts`).
 *
 * Stages (plan §2.3): `tools` runs the player's mining beam from its `ToolControl` (Mining mode, `fire`); `physics`
 * drifts chunks and runs the collector. Both are allocation-free (D-014) except on rare events that change Rapier
 * colliders (a fracture, a depletion) or rebuild the cache after a preset change. Randomness is `space.rng` or
 * per-id hashes, never `sim.rng` (plan §2.11). Positions are float64 world metres (D-011).
 *
 * Booms (M10 P5a12b): a def that lists `mining.booms` fires one beam per boom socket on this same model. Boom 0 is the
 * one beam above (its target, chip timer, the fracture machine and its heat, the readout), fired from the first boom
 * socket with `booms.rangeM` and its heat × `booms.heatShare`; booms 1..N-1 each chip the first rock or fragment on
 * their own ray (no nav change), with their own chip timer and heat in fixed typed arrays (`boomsTools`). A boom
 * on a FRACTURE rock idles: one fracture machine per ship (boom 0's). A def without booms takes the single-beam path
 * from `beam.socket` and `beam.range`, byte for byte as before.
 *
 * Decisions (reported to the wave gate): fragments always CHIP and never fracture again; the burst's damage
 * distance is measured from the rock's surface; the beam auto-targets the first rock it hits (and makes it the nav
 * target, emitting `navTarget`) only when the nav target is not a live rock or fragment; laser heat while chipping
 * uses `chip.chipPower`, while a fracture is engaged the smoothed laser power.
 */
import { applyDamage, createDamageResult, type DamageResult } from '../combat/damage'
import { emitEvent, EV } from '../combat/events'
import { logger } from '../core/logger'
import { SIM_DT } from '../core/loop'
import type { Vec3 } from '../core/math'
import { nextFloat } from '../core/rng'
import { TETHERSTONE_INDEX, WARE_COUNT, type MiningConfig } from '../data/m2Schemas'
import { MAX_MINING_BOOMS } from '../data/schemas'
import { assignBooms, clearMarks, createBoomMarks, dropMark, toggleMark, type BoomMarks } from './booms'
import { raySphere } from '../mining/beam'
import { createChipState, createChunkSpawn, drawWare, resetChip, stepChip, type ChipState, type ChunkSpawn } from '../mining/chip'
import { resistance as rockResistance, resistanceTier, rockComposition, rockOreMassT } from '../mining/composition'
import { createFractureState, FRACTURE_EVENT, resetFracture, stepFracture, windowWidth, type FractureState } from '../mining/fracture'
import { createHeatState, HEAT_EVENT, stepHeat, type HeatState } from '../mining/heat'
import { scanRock, type RockFacts } from '../mining/scanner'
import { removeFieldRock, restoreFieldRocks } from '../sim'
import { Life, Pos64, Rot, Ship } from '../world/components'
import { fieldIndexOf, generateField, type FieldLayout } from '../world/field'
import { clearChunks, createChunkState, spawnChunk, stepChunks, type ChunkState } from './chunks'
import { ToolControl } from './components'
import {
  clearNav,
  createChunkPool,
  createFragmentPool,
  createBoomReadout,
  createMiningReadout,
  type BoomReadout,
  MINING_MODE,
  NAV,
  setNav,
  TOOL,
  type FieldMixAt,
  type MiningReadout,
  type NavKind,
  type NavTarget,
  type RockDeltas,
  type RockFieldFacts,
  type RockScan,
  type RocksRuntime,
  type SpaceContext,
} from './contracts'
import { emitSpaceEvent, SEV } from './events'
import {
  clearFragments,
  createFragmentParent,
  createFragmentState,
  fragmentKey,
  fragmentMixOffset,
  removeFragment,
  spawnFragments,
  type FragmentParent,
  type FragmentState,
} from './fragments'
import { seededUnit } from './hash'

/** Hash salt of a rock's fracture-window start phase. */
const SALT_WINDOW_PHASE = 0x3a1d
/** Chunks spawn this far off the rock surface (m), so they start outside it. */
const CHUNK_SURFACE_OFFSET = 0.5
/** Remaining tonnes at or below this count as depleted. */
const DEPLETED_T = 1e-6

/** Everything the mining runtime keeps (one per space world). */
interface RocksState {
  readonly ctx: SpaceContext
  readonly readout: MiningReadout
  readonly chunks: ChunkState
  readonly frag: FragmentState
  // Per-layout-index cache of the active field layout.
  layout: FieldLayout | null
  version: number
  touched: Uint8Array
  gone: Uint8Array
  massT: Float32Array
  remaining: Float64Array
  mix: Float32Array
  mode: Uint8Array
  resistance: Float32Array
  tier: Uint8Array
  /** The field grade multiplier each rock was composed with, 0 outside every field (MC-a6, the scanner's grade line). */
  grade: Float32Array
  /** The system's signature ware when the rock's field holds it, else -1 (MC-a6, the scanner's signature line). */
  signature: Int8Array
  /** Remaining tonnes of partly mined rocks by stable id (untouched = full; removed rocks are not listed). */
  readonly remainingById: Map<number, number>
  /** The full High layout, generated only when a load needs a parent the active preset lacks. */
  highLayout: FieldLayout | null
  /** M3 bridge: the fracture tetherstone chance at a rock's position (negative: the default), or null. */
  tetherChanceAt: ((x: number, y: number, z: number) => number) | null
  /** M8 bridge (MC-a5a): the galaxy field mix at a rock's position, or null. Read when a rock's composition is built (MC-a5b). */
  fieldMixAt: FieldMixAt | null
  /** Bumped by every `setFieldMixAt` (MC-a6b: the rock tint refreshes on it). */
  mixVersion: number
  /** HP socket offset (ship space, m) per ship def index, 3 per def. */
  readonly socket: Float64Array
  /** Boom sockets per def (P5a12b): `MAX_MINING_BOOMS` × 3 per def index (ship space, m); a missing socket sits at the origin. */
  readonly boomSocket: Float64Array
  /** Booms per def index (0 = no booms: the single beam). */
  readonly boomCount: Uint8Array
  /** Booms 1..N-1: chip timer, heat and overheat cooldown per boom slot (boom 0 uses `chip` and `fracture`). */
  readonly boomChip: Float64Array
  readonly boomHeat: Float64Array
  readonly boomCool: Float64Array
  /** Scratch the boom slots load into for `stepChip` / `stepHeat`. */
  readonly boomChipState: ChipState
  readonly boomHeatState: HeatState
  /** Boom targeting (P5a12c): the helm's marks, the booms' mark slots and the assignment scratch. */
  readonly marks: BoomMarks
  readonly aimSlot: Int8Array
  readonly aimHeads: Float64Array
  readonly aimCentres: Float64Array
  readonly aimRadii: Float64Array
  readonly aimUsable: Uint8Array
  readonly aimLoad: Uint8Array
  readonly boomReadout: BoomReadout
  // The laser.
  readonly chip: ChipState
  readonly spawn: ChunkSpawn
  /** Fracture machine and laser heat on one struct. */
  readonly fracture: FractureState
  engKind: NavKind
  engIndex: number
  /** Stable id of the engaged rock (guards against index reuse). */
  engId: number
  /** Seconds the engaged rock has been off the beam. */
  offTime: number
  // Scratch.
  readonly o: Vec3
  readonly d: Vec3
  readonly c: Vec3
  cr: number
  hitKind: NavKind
  hitIndex: number
  readonly mixScratch: Float32Array
  /** The field mix of the rock being composed (`loadFieldMix`). */
  readonly fieldScratch: Float32Array
  readonly facts: RockFacts
  readonly parent: FragmentParent
  readonly damage: DamageResult
  readonly keep: Float32Array
}

// ---------------------------------------------------------------------------------------------
// Rock cache
// ---------------------------------------------------------------------------------------------

/** Rebuilds the per-index cache for the sim's current layout (rare: a preset change or a load). Allocates. */
function rebuildCache(s: RocksState): void {
  const sim = s.ctx.sim
  const layout = sim.field
  const old = s.layout
  s.layout = layout
  s.version = sim.fieldVersion
  const n = layout ? layout.count : 0
  if (s.touched.length < n) {
    s.touched = new Uint8Array(n)
    s.gone = new Uint8Array(n)
    s.massT = new Float32Array(n)
    s.remaining = new Float64Array(n)
    s.mix = new Float32Array(n * WARE_COUNT)
    s.mode = new Uint8Array(n)
    s.resistance = new Float32Array(n)
    s.tier = new Uint8Array(n)
    s.grade = new Float32Array(n)
    s.signature = new Int8Array(n)
  }
  s.touched.fill(0)
  if (layout) {
    for (let i = 0; i < n; i++) s.gone[i] = sim.removedRocks.has(layout.id[i]) ? 1 : 0
  }
  // A new layout renumbers the rocks (a preset change, or the Reach's merged stream-in, R22i): every rock target
  // follows its rock by stable id (plan §2.5 ids), and is cleared when the new layout lacks it or it is gone.
  if (old !== null) remapRockTargets(s, old)
}

/** The new layout's index of the live rock that was `index` in `old`, else -1. */
function remappedRock(s: RocksState, old: FieldLayout, index: number): number {
  const layout = s.layout
  if (layout === null || index < 0 || index >= old.count) return -1
  const i = fieldIndexOf(layout, old.id[index])
  return i >= 0 && s.gone[i] === 0 ? i : -1
}

/**
 * Moves every rock target from its index in `old` to its rock's index in the new layout (see `rebuildCache`). A kept
 * fracture engagement keeps its progress; its rock's cache is refilled at once (the fracture step reads it).
 */
function remapRockTargets(s: RocksState, old: FieldLayout): void {
  const ctx = s.ctx
  const player = ctx.player
  if (player.nav.kind === NAV.rock) {
    const i = remappedRock(s, old, player.nav.index)
    if (i < 0) {
      clearNav(player.nav)
      emitSpaceEvent(ctx.events, SEV.navTarget, ctx.sim.playerEid, NAV.none, -1, 0, 0, 0, 0, 0, 0)
    } else {
      player.nav.index = i
    }
  }
  if (player.scanned.kind === NAV.rock) {
    const i = remappedRock(s, old, player.scanned.index)
    if (i < 0) clearNav(player.scanned)
    else player.scanned.index = i
  }
  if (s.readout.rock.kind === NAV.rock) {
    const i = remappedRock(s, old, s.readout.rock.index)
    if (i < 0) clearNav(s.readout.rock)
    else s.readout.rock.index = i
  }
  const marks = s.marks
  for (let j = marks.count - 1; j >= 0; j--) {
    if (marks.kind[j] !== NAV.rock) continue
    const i = remappedRock(s, old, marks.index[j])
    if (i < 0) dropMark(marks, j)
    else marks.index[j] = i
  }
  if (s.engKind === NAV.rock) {
    const i = remappedRock(s, old, s.engIndex)
    if (i < 0) {
      disengage(s)
    } else {
      s.engIndex = i
      ensureRock(s, i)
    }
  }
}

/** Refreshes the gone flags after `sim.fieldVersion` moved (a rock was removed or restored). */
function refreshGone(s: RocksState): void {
  const sim = s.ctx.sim
  const layout = s.layout
  s.version = sim.fieldVersion
  if (layout === null) return
  for (let i = 0; i < layout.count; i++) s.gone[i] = sim.removedRocks.has(layout.id[i]) ? 1 : 0
}

/** Brings the cache in line with the sim's layout and removed set. */
function syncCache(s: RocksState): void {
  const sim = s.ctx.sim
  if (sim.field !== s.layout) rebuildCache(s)
  else if (sim.fieldVersion !== s.version) refreshGone(s)
}

/** The fracture tetherstone chance of rock `i` of `layout`: the bridge's hook, else `mining.json`. */
function tetherChanceOf(s: RocksState, layout: FieldLayout, i: number): number {
  const at = s.tetherChanceAt
  const c = at === null ? -1 : at(layout.x[i], layout.y[i], layout.z[i])
  return c < 0 ? s.ctx.data.mining.tetherstone.chance : c
}

/**
 * MC-a5b: writes the galaxy field mix at rock `i` of `layout` into `s.fieldScratch` and returns its grade multiplier.
 * No hook, or `mix.fieldBlend` 0 with `gradeMassMult` off: zeros and 1 without asking (archetype only, today's rocks).
 * Called only when a composition is built, never per frame. Allocation-free (D-014).
 */
function loadFieldMix(s: RocksState, layout: FieldLayout, i: number): number {
  const at = s.fieldMixAt
  const mix = s.ctx.data.mining.mix
  if (at === null || (!(mix.fieldBlend > 0) && !mix.gradeMassMult)) {
    s.fieldScratch.fill(0)
    return 1
  }
  return at(layout.x[i], layout.y[i], layout.z[i], s.fieldScratch)
}

/** MC-e6b: the ore rock `i`'s field keeps through the `maxOres` cut (`FieldMixAt.keepAt`), -1 for none. Allocation-free. */
function keepWareOf(s: RocksState, layout: FieldLayout, i: number): number {
  const keepAt = s.fieldMixAt?.keepAt
  return keepAt === undefined ? -1 : keepAt(layout.x[i], layout.y[i], layout.z[i])
}

/** Computes rock `i`'s composition on first touch. */
function ensureRock(s: RocksState, i: number): void {
  if (s.touched[i] === 1) return
  const layout = s.layout
  if (layout === null) return
  const ctx = s.ctx
  const cfg = ctx.data.mining
  const id = layout.id[i]
  const seed = ctx.sim.seed
  const radius = layout.radius[i]
  const grade = loadFieldMix(s, layout, i)
  const tether = tetherChanceOf(s, layout, i)
  const keep = keepWareOf(s, layout, i)
  const mode = rockComposition(id, seed, layout.archetype[i], radius, cfg, ctx.data.wares.wares, s.mixScratch, tether, s.fieldScratch, grade, keep)
  const base = i * WARE_COUNT
  for (let w = 0; w < WARE_COUNT; w++) s.mix[base + w] = s.mixScratch[w]
  s.mode[i] = mode
  const full = rockOreMassT(radius, cfg, grade)
  s.massT[i] = full
  const left = s.remainingById.get(id)
  s.remaining[i] = left === undefined ? full : left
  const r = rockResistance(id, seed)
  s.resistance[i] = r
  s.tier[i] = resistanceTier(r, cfg.fracture.tiers)
  writeFieldFacts(s, layout, i, grade)
  s.touched[i] = 1
}

/**
 * MC-a6: stores rock `i`'s field grade (0 when its field mix in `s.fieldScratch` is empty: outside every field, or
 * the blend off) and the system's signature ore when that field holds it: the lookup's `signatureAt` the rock's
 * position (PF-4: an ice field holds ice, not the system's first signature ore), else its `signature`. Allocation-free.
 */
function writeFieldFacts(s: RocksState, layout: FieldLayout, i: number, grade: number): void {
  const field = s.fieldScratch
  let total = 0
  for (let w = 0; w < WARE_COUNT; w++) if (w !== TETHERSTONE_INDEX) total += field[w]
  const at = s.fieldMixAt
  const sig = at === null ? -1 : at.signatureAt !== undefined ? at.signatureAt(layout.x[i], layout.y[i], layout.z[i]) : (at.signature ?? -1)
  const inField = total > 0
  s.grade[i] = inField ? grade : 0
  s.signature[i] = inField && sig >= 0 && sig < WARE_COUNT && field[sig] > 0 ? sig : -1
}

/**
 * MC-a6b (the rock tint): rock `index`'s cached field facts into `out`; an untouched rock's are written first, from
 * the same lookup its composition will use (`ensureRock` writes them again on first touch). Allocation-free.
 */
function fieldFactsOf(s: RocksState, index: number, out: RockFieldFacts): boolean {
  syncCache(s)
  const layout = s.layout
  if (layout === null || index < 0 || index >= layout.count) {
    out.grade = 0
    out.signature = -1
    return false
  }
  if (s.touched[index] === 0) writeFieldFacts(s, layout, index, loadFieldMix(s, layout, index))
  out.grade = s.grade[index]
  out.signature = s.signature[index]
  return true
}

/** A live rock (active layout index) or fragment (pool slot). */
function targetAlive(s: RocksState, kind: number, index: number): boolean {
  if (kind === NAV.rock) return s.layout !== null && index >= 0 && index < s.layout.count && s.gone[index] === 0
  if (kind === NAV.fragment) return index >= 0 && index < s.frag.pool.high && s.frag.pool.alive[index] === 1
  return false
}

/** Writes the target's centre into `s.c` and its radius into `s.cr`. The target must be alive. */
function loadTarget(s: RocksState, kind: number, index: number): void {
  if (kind === NAV.rock) {
    const layout = s.layout
    if (layout === null) return
    s.c.x = layout.x[index]
    s.c.y = layout.y[index]
    s.c.z = layout.z[index]
    s.cr = layout.radius[index]
  } else {
    const pool = s.frag.pool
    s.c.x = pool.x[index]
    s.c.y = pool.y[index]
    s.c.z = pool.z[index]
    s.cr = pool.radius[index]
  }
}

/** Clears every nav, scan and readout reference to (kind, index); emits `navTarget` when the nav target was it. */
function clearTargetsAt(s: RocksState, kind: number, index: number): void {
  const ctx = s.ctx
  const player = ctx.player
  if (player.nav.kind === kind && player.nav.index === index) {
    clearNav(player.nav)
    emitSpaceEvent(ctx.events, SEV.navTarget, ctx.sim.playerEid, NAV.none, -1, 0, 0, 0, 0, 0, 0)
  }
  if (player.scanned.kind === kind && player.scanned.index === index) clearNav(player.scanned)
  if (s.readout.rock.kind === kind && s.readout.rock.index === index) clearNav(s.readout.rock)
  if (s.engKind === kind && s.engIndex === index) disengage(s)
}

/** Clears every reference to any target of `kind` (a preset change renumbers rocks; a load clears both kinds). */
function clearTargetsOfKind(s: RocksState, kind: number): void {
  const ctx = s.ctx
  const player = ctx.player
  if (player.nav.kind === kind) {
    clearNav(player.nav)
    emitSpaceEvent(ctx.events, SEV.navTarget, ctx.sim.playerEid, NAV.none, -1, 0, 0, 0, 0, 0, 0)
  }
  if (player.scanned.kind === kind) clearNav(player.scanned)
  if (s.readout.rock.kind === kind) clearNav(s.readout.rock)
  if (s.engKind === kind) disengage(s)
}

// ---------------------------------------------------------------------------------------------
// The beam
// ---------------------------------------------------------------------------------------------

/** Beam origin (socket offset `k` of `sockets`, in world) into `s.o` and the nose direction into `s.d`. */
function beamRay(s: RocksState, eid: number, sockets: Float64Array, k: number): void {
  const qx = Rot.x[eid]
  const qy = Rot.y[eid]
  const qz = Rot.z[eid]
  const qw = Rot.w[eid]
  const vx = sockets[k]
  const vy = sockets[k + 1]
  const vz = sockets[k + 2]
  // v' = v + w·t + q×t, t = 2·(q×v)
  const tx = 2 * (qy * vz - qz * vy)
  const ty = 2 * (qz * vx - qx * vz)
  const tz = 2 * (qx * vy - qy * vx)
  s.o.x = Pos64.x[eid] + vx + qw * tx + (qy * tz - qz * ty)
  s.o.y = Pos64.y[eid] + vy + qw * ty + (qz * tx - qx * tz)
  s.o.z = Pos64.z[eid] + vz + qw * tz + (qx * ty - qy * tx)
  // Nose = q · (0, 0, −1).
  s.d.x = -2 * (qx * qz + qw * qy)
  s.d.y = -2 * (qy * qz - qw * qx)
  s.d.z = -(1 - 2 * (qx * qx + qy * qy))
}

/** The first rock or fragment the beam ray hits within `range` into `s.hitKind` / `s.hitIndex` (linear scan). */
function firstHit(s: RocksState, range: number): void {
  s.hitKind = NAV.none
  s.hitIndex = -1
  let best = range
  const o = s.o
  const d = s.d
  const layout = s.layout
  if (layout !== null) {
    for (let i = 0; i < layout.count; i++) {
      if (s.gone[i] === 1) continue
      const t = raySphere(o.x, o.y, o.z, d.x, d.y, d.z, layout.x[i], layout.y[i], layout.z[i], layout.radius[i], best)
      if (t >= 0 && (t < best || s.hitKind === NAV.none)) {
        best = t
        s.hitKind = NAV.rock
        s.hitIndex = i
      }
    }
  }
  const pool = s.frag.pool
  for (let i = 0; i < pool.high; i++) {
    if (pool.alive[i] === 0) continue
    const t = raySphere(o.x, o.y, o.z, d.x, d.y, d.z, pool.x[i], pool.y[i], pool.z[i], pool.radius[i], best)
    if (t >= 0 && (t < best || s.hitKind === NAV.none)) {
      best = t
      s.hitKind = NAV.fragment
      s.hitIndex = i
    }
  }
}

/** Remaining tonnes of a live target. */
function remainingOf(s: RocksState, kind: number, index: number): number {
  return kind === NAV.rock ? s.remaining[index] : s.frag.pool.remainingT[index]
}

/** Copies a live target's mix into `s.mixScratch`. */
function loadMix(s: RocksState, kind: number, index: number): void {
  const src = kind === NAV.rock ? s.mix : s.frag.groupMix
  const base = kind === NAV.rock ? index * WARE_COUNT : fragmentMixOffset(s.frag, index)
  for (let w = 0; w < WARE_COUNT; w++) s.mixScratch[w] = base >= 0 ? src[base + w] : 0
}

/** Takes rock `i` out of the world (depleted, fractured or burst). */
function removeRock(s: RocksState, i: number): void {
  const layout = s.layout
  if (layout === null) return
  const id = layout.id[i]
  s.gone[i] = 1
  s.remaining[i] = 0
  s.remainingById.delete(id)
  removeFieldRock(s.ctx.sim, id)
  clearTargetsAt(s, NAV.rock, i)
}

/** Takes a depleted target out of the world. */
function deplete(s: RocksState, kind: number, index: number): void {
  if (kind === NAV.rock) {
    removeRock(s, index)
  } else {
    clearTargetsAt(s, NAV.fragment, index)
    removeFragment(s.frag, s.ctx.sim.physics, index)
  }
}

/**
 * CHIP: beam contact `t` metres along the ray on a live target (centre in `s.c`). Breaks off a chunk every chip
 * interval at the contact point, pushed out along the surface normal, and depletes the target when its mass runs out.
 */
function chipTarget(s: RocksState, eid: number, kind: number, index: number, t: number, chip: ChipState, boom: number): void {
  const ctx = s.ctx
  const cfg = ctx.data.mining
  loadMix(s, kind, index)
  const yieldMult = kind === NAV.fragment ? cfg.chip.fragmentYieldMult : 1
  if (!stepChip(chip, SIM_DT, ctx.mirror.tools.chipRateMult, yieldMult, s.mixScratch, cfg, ctx.rng, s.spawn)) return
  const left = remainingOf(s, kind, index)
  const tonnes = s.spawn.massT < left ? s.spawn.massT : left
  const px = s.o.x + s.d.x * t
  const py = s.o.y + s.d.y * t
  const pz = s.o.z + s.d.z * t
  let nx = px - s.c.x
  let ny = py - s.c.y
  let nz = pz - s.c.z
  const nl = Math.sqrt(nx * nx + ny * ny + nz * nz)
  if (nl > 1e-9) {
    nx /= nl
    ny /= nl
    nz /= nl
  } else {
    nx = -s.d.x
    ny = -s.d.y
    nz = -s.d.z
  }
  const speed = s.spawn.speed
  spawnChunk(
    s.chunks,
    px + nx * CHUNK_SURFACE_OFFSET,
    py + ny * CHUNK_SURFACE_OFFSET,
    pz + nz * CHUNK_SURFACE_OFFSET,
    nx * speed,
    ny * speed,
    nz * speed,
    s.spawn.ware,
    tonnes,
    ctx.mirror.gameTime,
    nextFloat(ctx.rng),
  )
  emitSpaceEvent(ctx.events, SEV.chipBreak, eid, index, kind, boom, px, py, pz, tonnes, 0)
  const rest = left - tonnes
  if (kind === NAV.rock) {
    s.remaining[index] = rest
    if (s.layout !== null) s.remainingById.set(s.layout.id[index], rest)
  } else {
    s.frag.pool.remainingT[index] = rest
  }
  if (rest <= DEPLETED_T) deplete(s, kind, index)
}

// ---------------------------------------------------------------------------------------------
// Fracture
// ---------------------------------------------------------------------------------------------

/** Ends the fracture engagement (progress and instability reset; the laser keeps its heat). */
function disengage(s: RocksState): void {
  s.engKind = NAV.none
  s.engIndex = -1
  s.engId = -1
  s.offTime = 0
  resetFracture(s.fracture, 0)
}

/** Engages the fracture machine on rock `i` (a new engagement starts from a seeded window phase). */
function engage(s: RocksState, i: number): void {
  const layout = s.layout
  if (layout === null) return
  const id = layout.id[i]
  s.offTime = 0
  if (s.engKind === NAV.rock && s.engIndex === i && s.engId === id) return
  s.engKind = NAV.rock
  s.engIndex = i
  s.engId = id
  resetFracture(s.fracture, 2 * Math.PI * seededUnit(s.ctx.sim.seed, id, SALT_WINDOW_PHASE))
}

/** Fills the fragment-parent scratch from rock `i` of `layout`. */
function loadParent(s: RocksState, layout: FieldLayout, i: number, mass: number, r: number, tier: number): void {
  const p = s.parent
  p.id = layout.id[i]
  p.x = layout.x[i]
  p.y = layout.y[i]
  p.z = layout.z[i]
  p.radius = layout.radius[i]
  p.massT = mass
  p.archetype = layout.archetype[i]
  p.resistance = r
  p.tier = tier
}

/** Success: rock `i` splits into its fragments (static balls, the parent's mix); the parent leaves the world. */
function fractureSuccess(s: RocksState, eid: number, i: number): void {
  const layout = s.layout
  if (layout === null) return
  const ctx = s.ctx
  const sim = ctx.sim
  const cfg = ctx.data.mining
  loadParent(s, layout, i, s.massT[i], s.resistance[i], s.tier[i])
  const n = spawnFragments(s.frag, sim.physics, s.parent, s.mix, i * WARE_COUNT, sim.seed, cfg, sim.data.field.colliderScale, null)
  const tether = s.mix[i * WARE_COUNT + TETHERSTONE_INDEX] * s.massT[i]
  const x = layout.x[i]
  const y = layout.y[i]
  const z = layout.z[i]
  disengage(s)
  removeRock(s, i)
  emitSpaceEvent(ctx.events, SEV.fractureSuccess, eid, i, n, NAV.rock, x, y, z, 0, 0)
  if (tether > 0) emitSpaceEvent(ctx.events, SEV.tetherstone, eid, 0, 0, 0, x, y, z, tether, 0)
}

/**
 * Burst: `burst.oreLoss` of rock `i`'s ore is lost, the rest scatters as `burst.chunks` chunks of at most
 * `maxChunkTonnes`, and the player within `burst.radius` of the surface takes `lerp(damage max, damage min, d / radius)`
 * (×1 vs shield, ×1 vs hull) with M1 hit events. The parent leaves the world.
 */
function fractureBurst(s: RocksState, eid: number, i: number): void {
  const layout = s.layout
  if (layout === null) return
  const ctx = s.ctx
  const sim = ctx.sim
  const cfg = ctx.data.mining
  const b = cfg.fracture.burst
  const rng = ctx.rng
  const cx = layout.x[i]
  const cy = layout.y[i]
  const cz = layout.z[i]
  const radius = layout.radius[i]
  loadMix(s, NAV.rock, i)
  const left = s.remaining[i] * (1 - b.oreLoss)
  let n = b.chunks[0] + Math.floor(nextFloat(rng) * (b.chunks[1] - b.chunks[0] + 1))
  if (n > b.chunks[1]) n = b.chunks[1]
  let per = n > 0 ? left / n : 0
  if (per > b.maxChunkTonnes) per = b.maxChunkTonnes
  const eject = cfg.chip.ejectSpeed
  const at = radius + CHUNK_SURFACE_OFFSET
  let spawned = 0
  for (let j = 0; j < n && per > 0; j++) {
    const u = 2 * nextFloat(rng) - 1
    const phi = 2 * Math.PI * nextFloat(rng)
    const ring = Math.sqrt(1 - u * u)
    const dx = ring * Math.cos(phi)
    const dy = u
    const dz = ring * Math.sin(phi)
    const speed = eject[0] + (eject[1] - eject[0]) * nextFloat(rng)
    const ware = drawWare(s.mixScratch, nextFloat(rng))
    const slot = spawnChunk(s.chunks, cx + dx * at, cy + dy * at, cz + dz * at, dx * speed, dy * speed, dz * speed, ware, per, ctx.mirror.gameTime, nextFloat(rng))
    if (slot >= 0) spawned++
  }
  let dealt = 0
  if (eid >= 0 && Life.alive[eid] === 1) {
    const px = Pos64.x[eid]
    const py = Pos64.y[eid]
    const pz = Pos64.z[eid]
    let nx = px - cx
    let ny = py - cy
    let nz = pz - cz
    const dc = Math.sqrt(nx * nx + ny * ny + nz * nz)
    let ds = dc - radius
    if (ds < 0) ds = 0
    if (ds <= b.radius) {
      if (dc > 1e-9) {
        nx /= dc
        ny /= dc
        nz /= dc
      }
      const raw = b.damage[1] + (b.damage[0] - b.damage[1]) * (ds / b.radius)
      const res = applyDamage(eid, raw, 1, 1, sim.time, s.damage)
      dealt = res.shieldAbsorbed + res.hullDamage
      if (res.shieldAbsorbed > 0) emitEvent(sim.events, EV.hitShield, px, py, pz, nx, ny, nz, eid, -1, 0, res.shieldAbsorbed)
      if (res.shieldDown) emitEvent(sim.events, EV.shieldDown, px, py, pz, 0, 0, 0, eid, -1, 0, 0)
      if (res.hullDamage > 0) emitEvent(sim.events, EV.hitHull, px, py, pz, nx, ny, nz, eid, -1, 0, res.hullDamage)
      if (res.killed) emitEvent(sim.events, EV.kill, px, py, pz, 0, 0, 0, eid, -1, 0, 0)
    }
  }
  disengage(s)
  removeRock(s, i)
  emitSpaceEvent(ctx.events, SEV.fractureBurst, eid, i, spawned, NAV.rock, cx, cy, cz, dealt, 0)
}

// ---------------------------------------------------------------------------------------------
// Stages
// ---------------------------------------------------------------------------------------------

/** The readout's fracture block while no fracture is engaged (tier and width of the nav rock when it fractures). */
function idleFractureReadout(s: RocksState, cfg: MiningConfig, maxTier: number): void {
  const f = s.readout.fracture
  f.active = false
  f.progress = 0
  f.instability = 0
  const nav = s.ctx.player.nav
  if (nav.kind === NAV.rock && targetAlive(s, NAV.rock, nav.index)) {
    ensureRock(s, nav.index)
    if (s.mode[nav.index] === MINING_MODE.fracture) {
      f.tier = s.tier[nav.index]
      f.tooHard = s.tier[nav.index] > maxTier
      f.windowWidth = windowWidth(s.resistance[nav.index], cfg)
      return
    }
  }
  f.tier = 0
  f.tooHard = false
  f.windowWidth = 0
}

/** The `tools` stage: the player's mining beam, chip, fracture, heat and bursts. Allocation-free. */
function rocksTools(s: RocksState): void {
  const ctx = s.ctx
  const sim = ctx.sim
  const cfg = ctx.data.mining
  const tools = ctx.mirror.tools
  const r = s.readout
  const events = ctx.events
  syncCache(s)
  const eid = sim.playerEid
  const live = eid >= 0 && Life.alive[eid] === 1
  const def = live ? Ship.defIndex[eid] : 0
  const booms = live ? s.boomCount[def] : 0
  // A boom hull fires only at the helm's marks (P5a12c, Q11): in Mining mode, hold not full; unmarked booms idle.
  if (booms > 0) aimBooms(s, eid, def, booms, ToolControl.mode[eid] === TOOL.mining && !holdFull(s))
  const wantBeam = booms > 0 ? s.aimSlot[0] >= 0 : live && ToolControl.mode[eid] === TOOL.mining && ToolControl.fire[eid] === 1
  const beamOn = wantBeam && s.fracture.cooldown <= 0
  let power = live ? ToolControl.power[eid] : 0
  if (power > tools.laserMaxPower) power = tools.laserMaxPower
  if (power < 0) power = 0
  const range = booms > 0 ? cfg.booms.rangeM : cfg.beam.range
  const heatShare = booms > 0 ? cfg.booms.heatShare : 1

  let contactKind: NavKind = NAV.none
  let contactIndex = -1
  let contactT = -1
  if (beamOn) {
    let kind: NavKind = NAV.none
    let index = -1
    if (booms > 0) {
      const j = s.aimSlot[0]
      aimBoom(s, 0, j)
      kind = s.marks.kind[j] as NavKind
      index = s.marks.index[j]
    } else {
      beamRay(s, eid, s.socket, def * 3)
      const nav = ctx.player.nav
      if ((nav.kind === NAV.rock || nav.kind === NAV.fragment) && targetAlive(s, nav.kind, nav.index)) {
        kind = nav.kind
        index = nav.index
      } else {
        firstHit(s, range)
        if (s.hitKind !== NAV.none) {
          kind = s.hitKind
          index = s.hitIndex
          setNav(nav, kind, index)
          emitSpaceEvent(events, SEV.navTarget, eid, kind, index, 0, 0, 0, 0, 0, 0)
        }
      }
    }
    if (kind !== NAV.none) {
      loadTarget(s, kind, index)
      const t = raySphere(s.o.x, s.o.y, s.o.z, s.d.x, s.d.y, s.d.z, s.c.x, s.c.y, s.c.z, s.cr, range)
      if (t >= 0) {
        contactKind = kind
        contactIndex = index
        contactT = t
      }
    }
    const reach = contactT >= 0 ? contactT : range
    r.beamFrom.x = s.o.x
    r.beamFrom.y = s.o.y
    r.beamFrom.z = s.o.z
    r.beamTo.x = s.o.x + s.d.x * reach
    r.beamTo.y = s.o.y + s.d.y * reach
    r.beamTo.z = s.o.z + s.d.z * reach
  }

  let mode: number = MINING_MODE.chip
  if (contactKind === NAV.rock) {
    ensureRock(s, contactIndex)
    mode = s.mode[contactIndex]
  }
  // Readout target before the tick's events can remove it.
  if (contactKind !== NAV.none) setNav(r.rock, contactKind, contactIndex)
  else clearNav(r.rock)
  r.mode = mode === MINING_MODE.fracture ? MINING_MODE.fracture : MINING_MODE.chip

  let fracturing = false
  if (contactKind !== NAV.none && mode === MINING_MODE.chip) {
    if (s.engKind !== NAV.none) disengage(s)
    chipTarget(s, eid, contactKind, contactIndex, contactT, s.chip, 0)
  } else {
    resetChip(s.chip)
  }
  if (contactKind === NAV.rock && mode === MINING_MODE.fracture) {
    engage(s, contactIndex)
    fracturing = true
  } else if (s.engKind !== NAV.none) {
    if (!targetAlive(s, s.engKind, s.engIndex)) disengage(s)
    else {
      s.offTime += SIM_DT
      if (s.offTime >= cfg.fracture.disengageSeconds) disengage(s)
    }
  }
  if (s.engKind !== NAV.none) {
    const i = s.engIndex
    const ev = stepFracture(s.fracture, fracturing ? power : 0, SIM_DT, s.resistance[i], tools.maxTier, s.tier[i], cfg, r.fracture)
    if (ev === FRACTURE_EVENT.start) {
      emitSpaceEvent(events, SEV.fractureStart, eid, i, 0, NAV.rock, s.c.x, s.c.y, s.c.z, 0, 0)
    } else if (ev === FRACTURE_EVENT.success) {
      fractureSuccess(s, eid, i)
    } else if (ev === FRACTURE_EVENT.burst) {
      fractureBurst(s, eid, i)
    }
  }
  if (s.engKind === NAV.none) idleFractureReadout(s, cfg, tools.maxTier)

  const hev = stepHeat(s.fracture, beamOn, (fracturing ? power : cfg.chip.chipPower) * heatShare, SIM_DT, tools.heatCapacityMult, cfg)
  if (hev === HEAT_EVENT.overheat) emitSpaceEvent(events, SEV.overheat, eid, 0, 0, 0, 0, 0, 0, 0, 0)
  else if (hev === HEAT_EVENT.cooled) emitSpaceEvent(events, SEV.cooled, eid, 0, 0, 0, 0, 0, 0, 0, 0)
  if (booms > 1) boomsTools(s, eid, booms, range, heatShare)
  coolIdleBooms(s, booms > 1 ? booms : 1)
  writeBoomReadout(s, booms, beamOn)

  r.beamOn = beamOn
  r.contact = r.rock.kind !== NAV.none
  r.power = power
  r.maxPower = tools.laserMaxPower
  r.heat = s.fracture.heat
  r.overheated = s.fracture.cooldown > 0
  r.cooldownLeft = s.fracture.cooldown > 0 ? s.fracture.cooldown : 0
  r.chipTimer = s.chip.timer
}

/** The hold has no room for the smallest chunk: the booms idle (P5a12c). */
function holdFull(s: RocksState): boolean {
  const m = s.ctx.mirror
  return m.cargoCapacityT - m.cargoUsedT < s.ctx.data.mining.chip.chunkMassT[0]
}

/**
 * Boom targeting (P5a12c): drops gone marks, loads each mark's centre and radius and each boom head, and assigns
 * every boom a mark slot (`s.aimSlot`, -1 idle; all idle unless `active`) within `booms.arcDeg` / `rangeM`.
 * Allocation-free.
 */
function aimBooms(s: RocksState, eid: number, def: number, n: number, active: boolean): void {
  const marks = s.marks
  for (let j = marks.count - 1; j >= 0; j--) if (!targetAlive(s, marks.kind[j], marks.index[j])) dropMark(marks, j)
  for (let k = 0; k < MAX_MINING_BOOMS; k++) s.aimSlot[k] = -1
  if (!active || marks.count === 0) return
  for (let j = 0; j < marks.count; j++) {
    loadTarget(s, marks.kind[j], marks.index[j])
    s.aimCentres[j * 3] = s.c.x
    s.aimCentres[j * 3 + 1] = s.c.y
    s.aimCentres[j * 3 + 2] = s.c.z
    s.aimRadii[j] = s.cr
    s.aimUsable[j] = 1
  }
  const base = def * MAX_MINING_BOOMS * 3
  for (let k = 0; k < n; k++) {
    beamRay(s, eid, s.boomSocket, base + k * 3)
    s.aimHeads[k * 3] = s.o.x
    s.aimHeads[k * 3 + 1] = s.o.y
    s.aimHeads[k * 3 + 2] = s.o.z
  }
  const b = s.ctx.data.mining.booms
  const cosHalf = Math.cos((b.arcDeg * Math.PI) / 360)
  assignBooms(n, s.aimHeads, s.d.x, s.d.y, s.d.z, marks.count, s.aimCentres, s.aimRadii, s.aimUsable, b.rangeM, cosHalf, s.aimLoad, s.aimSlot)
}

/** Boom `k`'s ray from its head toward mark slot `j`'s centre into `s.o` / `s.d` (after `aimBooms`). */
function aimBoom(s: RocksState, k: number, j: number): void {
  const ox = s.aimHeads[k * 3]
  const oy = s.aimHeads[k * 3 + 1]
  const oz = s.aimHeads[k * 3 + 2]
  const dx = s.aimCentres[j * 3] - ox
  const dy = s.aimCentres[j * 3 + 1] - oy
  const dz = s.aimCentres[j * 3 + 2] - oz
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz)
  s.o.x = ox
  s.o.y = oy
  s.o.z = oz
  if (len > 1e-9) {
    s.d.x = dx / len
    s.d.y = dy / len
    s.d.z = dz / len
  }
}

/**
 * Booms 1..n-1 of the player's def (P5a12b, aimed by P5a12c): each fires from its own head at the mark `aimBooms`
 * gave it, chips it with its own chip timer, and heats on its own (`heatShare` × one laser at `chip.chipPower`). A
 * boom with no mark, or on a FRACTURE mark, idles (one fracture machine per ship, boom 0's). Overheat and cooled
 * events carry the boom in `a`; chip breaks carry it in `c`. Allocation-free (D-014).
 */
function boomsTools(s: RocksState, eid: number, n: number, range: number, heatShare: number): void {
  const ctx = s.ctx
  const cfg = ctx.data.mining
  const tools = ctx.mirror.tools
  const heat = s.boomHeatState
  const chip = s.boomChipState
  const br = s.boomReadout
  for (let k = 1; k < n; k++) {
    heat.heat = s.boomHeat[k]
    heat.cooldown = s.boomCool[k]
    const j = s.aimSlot[k]
    let on = j >= 0 && heat.cooldown <= 0
    let chipping = false
    br.on[k] = 0
    if (on) {
      const kind = s.marks.kind[j]
      const index = s.marks.index[j]
      let mode: number = MINING_MODE.chip
      if (kind === NAV.rock) {
        ensureRock(s, index)
        mode = s.mode[index]
      }
      if (mode !== MINING_MODE.chip) {
        on = false
      } else {
        aimBoom(s, k, j)
        loadTarget(s, kind, index)
        const t = raySphere(s.o.x, s.o.y, s.o.z, s.d.x, s.d.y, s.d.z, s.c.x, s.c.y, s.c.z, s.cr, range)
        const reach = t >= 0 ? t : range
        br.on[k] = 1
        br.from[k * 3] = s.o.x
        br.from[k * 3 + 1] = s.o.y
        br.from[k * 3 + 2] = s.o.z
        br.to[k * 3] = s.o.x + s.d.x * reach
        br.to[k * 3 + 1] = s.o.y + s.d.y * reach
        br.to[k * 3 + 2] = s.o.z + s.d.z * reach
        if (t >= 0) {
          chipping = true
          chip.timer = s.boomChip[k]
          chipTarget(s, eid, kind, index, t, chip, k)
          s.boomChip[k] = chip.timer
        }
      }
    }
    if (!chipping) s.boomChip[k] = 0
    const hev = stepHeat(heat, on, cfg.chip.chipPower * heatShare, SIM_DT, tools.heatCapacityMult, cfg)
    s.boomHeat[k] = heat.heat
    s.boomCool[k] = heat.cooldown
    if (hev === HEAT_EVENT.overheat) emitSpaceEvent(ctx.events, SEV.overheat, eid, k, 0, 0, 0, 0, 0, 0, 0)
    else if (hev === HEAT_EVENT.cooled) emitSpaceEvent(ctx.events, SEV.cooled, eid, k, 0, 0, 0, 0, 0, 0, 0)
  }
}

/**
 * Review B1 (P5a12c): boom slots `from`..MAX-1 that no boom fired this tick (a non-boom hull, a hull with fewer booms,
 * or the player dead) keep cooling as boom 0 does, silently (no cue on a hull without them). Allocation-free.
 */
function coolIdleBooms(s: RocksState, from: number): void {
  const cfg = s.ctx.data.mining
  const heat = s.boomHeatState
  for (let k = from; k < MAX_MINING_BOOMS; k++) {
    s.boomChip[k] = 0
    if (s.boomHeat[k] === 0 && s.boomCool[k] === 0) continue
    heat.heat = s.boomHeat[k]
    heat.cooldown = s.boomCool[k]
    stepHeat(heat, false, 0, SIM_DT, s.ctx.mirror.tools.heatCapacityMult, cfg)
    s.boomHeat[k] = heat.heat
    s.boomCool[k] = heat.cooldown
  }
}

/** The per-boom HUD readout (P5a12c): heat, overheat, beams and marks; `count` 0 off a boom hull. */
function writeBoomReadout(s: RocksState, n: number, beamOn: boolean): void {
  const br = s.boomReadout
  br.count = n
  br.marks = s.marks.count
  if (n === 0) return
  const r = s.readout
  br.holdFull = holdFull(s)
  br.heat[0] = s.fracture.heat
  br.overheated[0] = s.fracture.cooldown > 0 ? 1 : 0
  br.on[0] = beamOn ? 1 : 0
  br.from[0] = r.beamFrom.x
  br.from[1] = r.beamFrom.y
  br.from[2] = r.beamFrom.z
  br.to[0] = r.beamTo.x
  br.to[1] = r.beamTo.y
  br.to[2] = r.beamTo.z
  for (let k = 0; k < MAX_MINING_BOOMS; k++) {
    br.mark[k] = s.aimSlot[k]
    if (k === 0) continue
    br.heat[k] = s.boomHeat[k]
    br.overheated[k] = s.boomCool[k] > 0 ? 1 : 0
    if (k >= n) br.on[k] = 0
  }
  writeMarkPositions(s)
}

/** The marked rocks' centres and radii for the HUD rings, read in place (`s.c` is left alone). Allocation-free. */
function writeMarkPositions(s: RocksState): void {
  const br = s.boomReadout
  const m = s.marks
  const layout = s.layout
  const pool = s.frag.pool
  for (let i = 0; i < m.count; i++) {
    const j = m.index[i]
    if (m.kind[i] === NAV.rock) {
      if (layout === null) continue
      br.markPos[i * 3] = layout.x[j]
      br.markPos[i * 3 + 1] = layout.y[j]
      br.markPos[i * 3 + 2] = layout.z[j]
      br.markR[i] = layout.radius[j]
    } else {
      br.markPos[i * 3] = pool.x[j]
      br.markPos[i * 3 + 1] = pool.y[j]
      br.markPos[i * 3 + 2] = pool.z[j]
      br.markR[i] = pool.radius[j]
    }
  }
}

/** The `physics` stage: chunk drift, expiry, the collector cone and capture. Allocation-free. */
function rocksPhysics(s: RocksState): void {
  const ctx = s.ctx
  stepChunks(s.chunks, ctx.sim.playerEid, ctx.mirror, ctx.events, ctx.data.mining, ctx.mirror.gameTime)
}

// ---------------------------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------------------------

/**
 * The live rock or fragment nearest the ray within `coneRad` (angle to its edge) and `maxRange` (surface
 * distance), else the nearest within `maxRange`. Allocation-free.
 */
function pickRock(
  s: RocksState,
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  coneRad: number,
  maxRange: number,
  out: NavTarget,
): boolean {
  syncCache(s)
  let coneKind: NavKind = NAV.none
  let coneIndex = -1
  let coneBest = coneRad
  let nearKind: NavKind = NAV.none
  let nearIndex = -1
  let nearBest = maxRange
  const dl = Math.sqrt(dx * dx + dy * dy + dz * dz)
  const layout = s.layout
  const pool = s.frag.pool
  const nRocks = layout !== null ? layout.count : 0
  const total = nRocks + pool.high
  for (let j = 0; j < total; j++) {
    let cx: number
    let cy: number
    let cz: number
    let cr: number
    let kind: NavKind
    let index: number
    if (j < nRocks && layout !== null) {
      if (s.gone[j] === 1) continue
      cx = layout.x[j]
      cy = layout.y[j]
      cz = layout.z[j]
      cr = layout.radius[j]
      kind = NAV.rock
      index = j
    } else {
      index = j - nRocks
      if (pool.alive[index] === 0) continue
      cx = pool.x[index]
      cy = pool.y[index]
      cz = pool.z[index]
      cr = pool.radius[index]
      kind = NAV.fragment
    }
    const vx = cx - ox
    const vy = cy - oy
    const vz = cz - oz
    const dist = Math.sqrt(vx * vx + vy * vy + vz * vz)
    const surface = dist - cr
    if (surface > maxRange) continue
    if (surface <= nearBest) {
      nearBest = surface
      nearKind = kind
      nearIndex = index
    }
    if (dist <= cr || dl === 0) continue
    let cos = (vx * dx + vy * dy + vz * dz) / (dist * dl)
    if (cos > 1) cos = 1
    if (cos < -1) cos = -1
    const angle = Math.acos(cos) - Math.asin(cr / dist)
    if (angle <= coneBest) {
      coneBest = angle
      coneKind = kind
      coneIndex = index
    }
  }
  if (coneKind !== NAV.none) {
    setNav(out, coneKind, coneIndex)
    return true
  }
  if (nearKind !== NAV.none) {
    setNav(out, nearKind, nearIndex)
    return true
  }
  return false
}

/** Scans a live rock or fragment into `out`. */
function scanTarget(s: RocksState, kind: NavKind, index: number, scannerMk: number, out: RockScan): boolean {
  syncCache(s)
  if (!targetAlive(s, kind, index)) return false
  const f = s.facts
  if (kind === NAV.rock) {
    ensureRock(s, index)
    f.id = s.layout !== null ? s.layout.id[index] : 0
    f.massT = s.remaining[index]
    f.mode = s.mode[index] === MINING_MODE.fracture ? MINING_MODE.fracture : MINING_MODE.chip
    f.resistance = s.resistance[index]
    f.tier = s.tier[index]
    f.grade = s.grade[index]
    f.signature = s.signature[index]
  } else {
    const pool = s.frag.pool
    const g = s.frag.group[index]
    f.id = fragmentKey(pool.parent[index], pool.k[index])
    f.massT = pool.remainingT[index]
    f.mode = MINING_MODE.chip
    f.resistance = g >= 0 ? s.frag.groupResistance[g] : 0
    f.tier = g >= 0 ? s.frag.groupTier[g] : 0
    f.grade = 0
    f.signature = -1
  }
  loadMix(s, kind, index)
  const ctx = s.ctx
  scanRock(f, ctx.sim.seed, kind, index, scannerMk, ctx.data.mining, ctx.data.wares.wares, out)
  return true
}

// ---------------------------------------------------------------------------------------------
// Deltas (save and load; not per tick, may allocate)
// ---------------------------------------------------------------------------------------------

function exportRockDeltas(s: RocksState): RockDeltas {
  const sim = s.ctx.sim
  const removed = Array.from(sim.removedRocks).sort((a, b) => a - b)
  const remaining: [number, number][] = []
  for (const [id, t] of s.remainingById) if (!sim.removedRocks.has(id)) remaining.push([id, t])
  remaining.sort((a, b) => a[0] - b[0])
  const frag = s.frag
  const pool = frag.pool
  const fractured: RockDeltas['fractured'] = []
  for (let g = 0; g < frag.groupAlive.length; g++) {
    if (frag.groupAlive[g] === 0) continue
    const fragments: [number, number][] = []
    for (let i = 0; i < pool.high; i++) if (pool.alive[i] === 1 && frag.group[i] === g) fragments.push([pool.k[i], pool.remainingT[i]])
    fragments.sort((a, b) => a[0] - b[0])
    fractured.push({ parent: frag.groupParent[g], fragments })
  }
  fractured.sort((a, b) => a.parent - b.parent)
  return { remaining, removed, fractured }
}

/** Finds the parent rock by stable id: in the active layout, else in the full High layout (generated once). */
function parentLayout(s: RocksState, id: number): { layout: FieldLayout; index: number } | null {
  const sim = s.ctx.sim
  if (sim.field !== null) {
    const i = fieldIndexOf(sim.field, id)
    if (i >= 0) return { layout: sim.field, index: i }
  }
  s.highLayout ??= generateField(sim.data.field, sim.seed, 'high', sim.ai.defs.patrolCenter)
  const i = fieldIndexOf(s.highLayout, id)
  return i >= 0 ? { layout: s.highLayout, index: i } : null
}

function importRockDeltas(s: RocksState, deltas: RockDeltas): void {
  const ctx = s.ctx
  const sim = ctx.sim
  const cfg = ctx.data.mining
  clearChunks(s.chunks)
  clearFragments(s.frag, sim.physics)
  disengage(s)
  s.fracture.heat = 0
  s.fracture.cooldown = 0
  resetChip(s.chip)
  s.boomChip.fill(0)
  s.boomHeat.fill(0)
  s.boomCool.fill(0)
  clearMarks(s.marks)
  clearTargetsOfKind(s, NAV.rock)
  clearTargetsOfKind(s, NAV.fragment)
  const ro = s.readout
  clearNav(ro.rock)
  ro.beamOn = false
  ro.contact = false
  ro.heat = 0
  ro.overheated = false
  ro.cooldownLeft = 0
  ro.chipTimer = 0
  Object.assign(ro.fracture, createMiningReadout().fracture)

  restoreFieldRocks(sim)
  for (const id of deltas.removed) removeFieldRock(sim, id)
  for (const f of deltas.fractured) removeFieldRock(sim, f.parent)
  s.remainingById.clear()
  for (const [id, t] of deltas.remaining) if (!sim.removedRocks.has(id)) s.remainingById.set(id, t)
  s.layout = null
  syncCache(s)

  const maxK = s.keep.length
  for (const f of deltas.fractured) {
    const found = parentLayout(s, f.parent)
    if (found === null) {
      logger.warn(`rocks.importDeltas: fractured parent ${f.parent} is not in the field; its fragments are dropped`)
      continue
    }
    s.keep.fill(-1)
    for (const [k, t] of f.fragments) if (k >= 0 && k < maxK) s.keep[k] = t
    const { layout, index } = found
    const id = layout.id[index]
    const radius = layout.radius[index]
    const grade = loadFieldMix(s, layout, index)
    const tether = tetherChanceOf(s, layout, index)
    const keep = keepWareOf(s, layout, index)
    rockComposition(id, sim.seed, layout.archetype[index], radius, cfg, ctx.data.wares.wares, s.mixScratch, tether, s.fieldScratch, grade, keep)
    const r = rockResistance(id, sim.seed)
    loadParent(s, layout, index, rockOreMassT(radius, cfg, grade), r, resistanceTier(r, cfg.fracture.tiers))
    spawnFragments(s.frag, sim.physics, s.parent, s.mixScratch, 0, sim.seed, cfg, sim.data.field.colliderScale, s.keep)
  }
}

// ---------------------------------------------------------------------------------------------
// The runtime
// ---------------------------------------------------------------------------------------------

/** Beam socket offsets per ship def (ship space), from the asset manifest; a missing socket sits at the origin. */
function socketOffsets(ctx: SpaceContext): Float64Array {
  const defs = ctx.sim.defs
  const out = new Float64Array(defs.length * 3)
  const name = ctx.data.mining.beam.socket
  for (let i = 0; i < defs.length; i++) {
    const entry = ctx.game.assets.models.find((m) => m.id === defs[i].assetId)
    const socket = entry?.sockets.find((k) => k.name === name)
    if (!socket) continue
    out[i * 3] = socket.position[0]
    out[i * 3 + 1] = socket.position[1]
    out[i * 3 + 2] = socket.position[2]
  }
  return out
}

/**
 * Boom socket offsets per ship def (P5a12b; ship space, `MAX_MINING_BOOMS` × 3 per def) from the def's `mining.booms`
 * and its model's sockets (a missing socket sits at the origin), and the boom count per def (0 = no booms).
 */
function boomOffsets(ctx: SpaceContext): { socket: Float64Array; count: Uint8Array } {
  const defs = ctx.sim.defs
  const socket = new Float64Array(defs.length * MAX_MINING_BOOMS * 3)
  const count = new Uint8Array(defs.length)
  for (let i = 0; i < defs.length; i++) {
    const booms = defs[i].mining?.booms
    if (!booms) continue
    // Review B5: the def schema caps `mining.booms` at MAX_MINING_BOOMS; a longer list here is a bypassed schema.
    if (booms.length > MAX_MINING_BOOMS) throw new Error(`ship ${defs[i].id}: ${booms.length} mining booms (max ${MAX_MINING_BOOMS})`)
    const n = booms.length
    count[i] = n
    const entry = ctx.game.assets.models.find((m) => m.id === defs[i].assetId)
    for (let k = 0; k < n; k++) {
      const found = entry?.sockets.find((m) => m.name === booms[k])
      if (!found) continue
      const o = (i * MAX_MINING_BOOMS + k) * 3
      socket[o] = found.position[0]
      socket[o + 1] = found.position[1]
      socket[o + 2] = found.position[2]
    }
  }
  return { socket, count }
}

export function createRocksRuntime(ctx: SpaceContext): RocksRuntime {
  const mining = ctx.data.mining
  const chunkPool = createChunkPool(mining.chip.poolCapacity)
  const fragmentPool = createFragmentPool(mining.fracture.fragmentPoolCapacity)
  const maxFragments = mining.fracture.fragments[1]
  const mixScratch = new Float32Array(WARE_COUNT)
  const booms = boomOffsets(ctx)
  const s: RocksState = {
    ctx,
    readout: createMiningReadout(),
    chunks: createChunkState(chunkPool),
    frag: createFragmentState(fragmentPool, maxFragments),
    layout: null,
    version: -1,
    touched: new Uint8Array(0),
    gone: new Uint8Array(0),
    massT: new Float32Array(0),
    remaining: new Float64Array(0),
    mix: new Float32Array(0),
    mode: new Uint8Array(0),
    resistance: new Float32Array(0),
    tier: new Uint8Array(0),
    grade: new Float32Array(0),
    signature: new Int8Array(0),
    remainingById: new Map<number, number>(),
    highLayout: null,
    tetherChanceAt: null,
    fieldMixAt: null,
    mixVersion: 0,
    socket: socketOffsets(ctx),
    boomSocket: booms.socket,
    boomCount: booms.count,
    boomChip: new Float64Array(MAX_MINING_BOOMS),
    boomHeat: new Float64Array(MAX_MINING_BOOMS),
    boomCool: new Float64Array(MAX_MINING_BOOMS),
    boomChipState: createChipState(),
    boomHeatState: createHeatState(),
    marks: createBoomMarks(),
    aimSlot: new Int8Array(MAX_MINING_BOOMS).fill(-1),
    aimHeads: new Float64Array(MAX_MINING_BOOMS * 3),
    aimCentres: new Float64Array(MAX_MINING_BOOMS * 3),
    aimRadii: new Float64Array(MAX_MINING_BOOMS),
    aimUsable: new Uint8Array(MAX_MINING_BOOMS),
    aimLoad: new Uint8Array(MAX_MINING_BOOMS),
    boomReadout: createBoomReadout(MAX_MINING_BOOMS),
    chip: createChipState(),
    spawn: createChunkSpawn(),
    fracture: createFractureState(),
    engKind: NAV.none,
    engIndex: -1,
    engId: -1,
    offTime: 0,
    o: { x: 0, y: 0, z: 0 },
    d: { x: 0, y: 0, z: -1 },
    c: { x: 0, y: 0, z: 0 },
    cr: 0,
    hitKind: NAV.none,
    hitIndex: -1,
    mixScratch,
    fieldScratch: new Float32Array(WARE_COUNT),
    // The scanner reads the mix through the facts struct: it shares the mix scratch.
    facts: { id: 0, massT: 0, mix: mixScratch, mode: MINING_MODE.chip, resistance: 0, tier: 0, grade: 0, signature: -1 },
    parent: createFragmentParent(),
    damage: createDamageResult(),
    keep: new Float32Array(Math.max(16, maxFragments)),
  }
  s.readout.booms = s.boomReadout
  syncCache(s)
  return {
    readout: s.readout,
    marks: s.marks,
    boomCount(): number {
      const eid = ctx.sim.playerEid
      return eid >= 0 && Life.alive[eid] === 1 ? s.boomCount[Ship.defIndex[eid]] : 0
    },
    toggleMark(kind, index): number {
      syncCache(s)
      const eid = ctx.sim.playerEid
      if (eid < 0 || !targetAlive(s, kind, index)) return 0
      loadTarget(s, kind, index)
      const dx = s.c.x - Pos64.x[eid]
      const dy = s.c.y - Pos64.y[eid]
      const dz = s.c.z - Pos64.z[eid]
      if (Math.sqrt(dx * dx + dy * dy + dz * dz) - s.cr > mining.booms.rangeM) return 0
      return toggleMark(s.marks, kind, index)
    },
    chunks: chunkPool,
    fragments: fragmentPool,
    pick(ox, oy, oz, dx, dy, dz, coneRad, maxRange, out): boolean {
      return pickRock(s, ox, oy, oz, dx, dy, dz, coneRad, maxRange, out)
    },
    scan(kind, index, scannerMk, out): boolean {
      return scanTarget(s, kind, index, scannerMk, out)
    },
    position(kind, index, out): boolean {
      syncCache(s)
      if (!targetAlive(s, kind, index)) return false
      loadTarget(s, kind, index)
      out.x = s.c.x
      out.y = s.c.y
      out.z = s.c.z
      return true
    },
    radius(kind, index): number {
      syncCache(s)
      if (!targetAlive(s, kind, index)) return 0
      loadTarget(s, kind, index)
      return s.cr
    },
    exportDeltas(): RockDeltas {
      return exportRockDeltas(s)
    },
    importDeltas(deltas: RockDeltas): void {
      importRockDeltas(s, deltas)
    },
    setTetherstoneChance(chanceAt): void {
      s.tetherChanceAt = chanceAt
    },
    setFieldMixAt(mixAt): void {
      s.fieldMixAt = mixAt
      s.mixVersion++
    },
    fieldFacts(index, out): boolean {
      return fieldFactsOf(s, index, out)
    },
    get fieldMixVersion(): number {
      return s.mixVersion
    },
    tools: (): void => rocksTools(s),
    physics: (): void => rocksPhysics(s),
  }
}
